// @flow
import _get from 'lodash/get';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import Button from '@localytics/loc-components/lib/Button';
import Sidebar from '@localytics/loc-components/lib/Sidebar';
import { OrchestrationTable } from './components/OrchestrationTable';
import OrchEmptyState from './components/OrchEmptyState';
import {
  VERSION_STATE_MAP,
  VERSION_STATE_OPEN,
  VERSION_STATE_DRAFT,
  hasActiveVersion
} from './constants/ApiTypes';
import * as ApiActions from './actions/api';
import './ChooseOrchPage.scss';
import { getQueryParams, ALL_PATH } from './util/RouterUtils';
import { RAILS_ENDPOINT_PATH } from './config';
import AbsoluteLinkWrapper from './components/AbsoluteLinkWrapper';

declare var ll: Function;

type PropTypes = {
  orgId: number,
  orchestrationData: Array<{
    name: string,
    orgId: number,
    orchId: number,
    versionId: number,
    editingState: $Keys<typeof VERSION_STATE_MAP>,
    hasOnlyDraftVersions: boolean,
    versionNumber: number,
    updatedAt: string,
    activeUsers: number,
    totalUsers: number,
    appGroupName: string
  }>,
  orchestrationsLoaded: boolean,
  userCountsStatus: string,
  authReady: boolean,
  orchestrationsError: boolean,
  getOrchestrations: Function,
  getUserCounts: Function,
  deleteOrchestration: Function,
  noAppGroups: boolean,
  noEventMappings: boolean,
  unauthorized: boolean,
  location: Object,
  history: Object
};

type StateTypes = {
  isSearching: boolean
};

class ChooseOrchPage extends React.Component<PropTypes, StateTypes> {
  constructor(props: PropTypes) {
    super(props);
    this.state = {
      isSearching: false
    };
    ll('tagScreen', 'Workflows');
    ll('tagEvent', 'Workflows - Visit Workflows Page');
  }
  componentWillMount() {
    if (this.props.authReady) {
      this.props.getOrchestrations(this.props.orgId);
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      orchestrationsLoaded,
      userCountsStatus,
      orchestrationData,
      orgId
    } = nextProps;
    if (orchestrationsLoaded && !userCountsStatus && orchestrationData.length) {
      const orchestrationIds = orchestrationData.map(d => d.orchId);
      this.props.getUserCounts(orgId, orchestrationIds);
    }
  }
  emptyStates = {
    apps: {
      title: 'You haven’t grouped any apps together yet!',
      subtitle:
        'By grouping apps, you will be able to send a single campaign message across multiple platforms.',
      link: `${RAILS_ENDPOINT_PATH}settings/app_groups`,
      linktext: 'Create an App Group',
      absolute: true
    },
    events: {
      title: 'You haven’t mapped any events together yet!',
      subtitle:
        'By mapping events together across platforms, you will be able to reference multiple events as one global event, and use these mappings to trigger workflows.',
      link: `${RAILS_ENDPOINT_PATH}settings/org_events`,
      linktext: 'Create an Event Mapping',
      absolute: true
    },
    orchestration: {
      title: 'You haven’t created any workflows yet!',
      subtitle:
        'Create workflows to trigger a cascade of messages based on events performed and attributes met by users across multiple plaforms.',
      link: `/new?orgId=${this.props.orgId}`,
      linktext: 'Create a Workflow',
      absolute: false
    },
    search: {
      title: 'There were no results for your search criteria',
      subtitle: 'Try changing your search',
      link: '',
      linktext: '',
      absolute: false
    }
  };

  searchCallback = (isSearching: boolean) => {
    this.setState({
      isSearching
    });
  };

  render() {
    const {
      orgId,
      orchestrationData,
      orchestrationsLoaded,
      orchestrationsError,
      deleteOrchestration,
      noAppGroups,
      noEventMappings,
      unauthorized,
      location,
      history
    } = this.props;

    let emptyKey = 'orchestration';
    emptyKey =
      orchestrationsLoaded && !orchestrationData.length
        ? 'orchestration'
        : emptyKey;
    emptyKey = noEventMappings ? 'events' : emptyKey;
    emptyKey = noAppGroups ? 'apps' : emptyKey;
    emptyKey = this.state.isSearching ? 'search' : emptyKey;
    const emptyStateObj = this.emptyStates[emptyKey];
    const emptyStateButtonLayout = this.state.isSearching ? null : (
      <div className="OrchEmptyState__ButtonContainer">
        <AbsoluteLinkWrapper
          href={emptyStateObj.link}
          absolute={emptyStateObj.absolute}
        >
          <Button
            className="ChooseOrchPage__NewButton"
            type="primary"
            label={emptyStateObj.linktext}
          />
        </AbsoluteLinkWrapper>
      </div>
    );
    const createNewButton =
      unauthorized || noAppGroups || noEventMappings ? null : (
        <Link to={`/new?orgId=${orgId}`}>
          <Button
            className="ChooseOrchPage__NewButton"
            type="primary"
            label="Create New"
          />
        </Link>
      );
    const goToWorkflowSetupButton =
      unauthorized || !orchestrationsLoaded ? null : (
        <div className="ChooseOrchPage__AppGroupsLinkContainer">
          <a
            className="ChooseOrchPage__AppGroupsLink"
            href="/settings/app_groups"
          >
            Go to Workflow Setup
          </a>
        </div>
      );

    const unauthorizedMessage = (
      <div className="ChooseOrchPage__PermissionNotice">
        <h3>
          We’re sorry but you don’t have permission to view this page yet!
        </h3>
        <div>
          Please contact your administrator if you think you should have access
          to Workflows.
        </div>
      </div>
    );

    return [
      <Sidebar
        key="sidebar"
        onLogoClick={() =>
          history.push({ pathname: ALL_PATH, search: location.search })
        }
        homePath={ALL_PATH}
        currentPath={location.pathname}
      />,
      <div className="Page__Body" key="body">
        <div
          className="Page__Content Page__Content--flexible"
          style={{ justifyContent: 'flex-start', padding: '10px 0 0 50px' }}
        >
          <div className="ChooseOrchPage__Content">
            <div className="ChooseOrchPage__HeaderRow">
              <div className="ChooseOrchPage__HeaderRow--left">
                <h1>MARKETING</h1>
                <h6>Workflows</h6>
              </div>
              <div className="ChooseOrchPage__HeaderRow--right">
                {goToWorkflowSetupButton}
              </div>
            </div>
            <span className="ChooseOrchPage__Text">
              Create workflows to automate marketing messages and design
              campaigns in response to user actions.
            </span>
            <div className="ChooseOrchPage__NewButtonContainer">
              {createNewButton}
            </div>
            {unauthorized ? (
              unauthorizedMessage
            ) : (
              <OrchestrationTable
                identifier={`${Date.now()}`}
                tableData={orchestrationData}
                orchestrationsLoaded={orchestrationsLoaded}
                orchestrationsError={orchestrationsError}
                deleteOrchestration={deleteOrchestration}
                isSearching={this.searchCallback}
                emptyState={
                  <OrchEmptyState
                    title={emptyStateObj.title}
                    subtitle={emptyStateObj.subtitle}
                  >
                    {emptyStateButtonLayout}
                  </OrchEmptyState>
                }
                orgId={orgId}
              />
            )}
          </div>
        </div>
      </div>
    ];
  }
}

const mapStateToProps = (state, ownProps) => {
  const { orgId } = getQueryParams(ownProps.location);

  const {
    api: {
      orchestrations = [],
      orchestrationsLoaded = false,
      orchestrationsError = false,
      userCountsByOrchestrationId = {},
      userCountsStatus = null,
      userCountsError = {},
      unauthorized = false,
      authReady
    },
    railsApi: { appGroupsById = {}, appGroups = [], eventMappings = [] }
  } = state;
  // TODO: use endpint that gets all versions for an org
  const orchestrationData = orchestrations
    .filter(o => o.orchestration_versions.length > 0)
    .map(o => {
      const {
        orchestration_versions: orchVersions = [],
        name,
        id: orchId,
        updated_at: updatedAt,
        created_at: createdAt
      } = o;

      const orchData = {
        name: name || '',
        orchId,
        orgId,
        updatedAt: updatedAt || createdAt,
        activeUsers: _get(
          userCountsByOrchestrationId,
          `${orchId}.active_users`,
          '–'
        ),
        totalUsers: _get(
          userCountsByOrchestrationId,
          `${orchId}.total_users`,
          '–'
        ),
        versionId: orchVersions[orchVersions.length - 1] || 1,
        editingState: VERSION_STATE_DRAFT,
        versionNumber: 0,
        appGroupName: ''
      };

      const versionData =
        orchVersions.length > 0
          ? orchVersions.map(orchVersion => {
              const {
                id: orchVersionId,
                state: verState,
                version_number: verNumber,
                updated_at: verUpdatedAt,
                created_at: verCreatedAt,
                app_group_id: appGroupId
              } = orchVersion;
              return {
                ...orchData,
                versionId: orchVersionId || orchData.versionId,
                editingState: verState || orchData.editingState,
                hasOnlyDraftVersions: !hasActiveVersion(orchVersions),
                versionNumber: verNumber || orchData.versionNumber,
                updatedAt: verUpdatedAt || verCreatedAt || orchData.updatedAt,
                appGroupName:
                  (appGroupId &&
                    appGroupsById[appGroupId] &&
                    appGroupsById[appGroupId].name) ||
                  orchData.appGroupName
              };
            })
          : [orchData];

      const activeVersion = versionData.find(
        v => v.editingState === VERSION_STATE_OPEN
      );
      if (activeVersion) {
        return activeVersion;
      }

      return versionData.sort((a, b) => {
        if (a.updatedAt > b.updatedAt) {
          return -1;
        } else if (a.updatedAt < b.updatedAt) {
          return 1;
        }
        return 0;
      })[0];
    });
  return {
    orgId,
    orchestrationsLoaded,
    orchestrationsError,
    orchestrationData,
    userCountsStatus,
    userCountsError,
    authReady,
    noAppGroups: !appGroups.length,
    noEventMappings: !eventMappings.length,
    unauthorized
  };
};

const mapDispatchToProps = {
  ...ApiActions
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ChooseOrchPage)
);



// WEBPACK FOOTER //
// ./src/ChooseOrchPage.jsx